import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useIsCommunityAdmin } from "@circle-react/contexts/punditUserContext";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";

import { t } from "@/i18n-js/instance";
import { useIsAdminArea } from "@/react/components/SettingsApp/CompassCompat/useIsAdminArea";
import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";

import { useIsCircleAiEnabled } from "../useCircleAiFeatureFlags";
import { useCircleAiStore } from "../useCircleAiStore";
import { useIsCircleAiPageRoute } from "../useIsCircleAiPageRoute";
import { useOpenCircleAi } from "../useOpenCircleAi";
import type { DockNavItem, DockNavSection } from "./types";
import { useInboxHasUnread } from "./useInboxHasUnread";

const getSkillsItem = (): DockNavItem => ({
  id: "skills",
  label: t("circle_ai_skills.nav.skills"),
  icon: "ai-box",
  activeIcon: "ai-box-filled",
  to: "/ai/skills",
  exact: false,
});

export const usePrimaryNavSection = (): DockNavSection => {
  const isAdmin = useIsCommunityAdmin();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const conversationPanelState = useCircleAIShellStore(
    s => s.conversationPanelState,
  );
  const setConversationPanelState = useCircleAIShellStore(
    s => s.setConversationPanelState,
  );
  const setAdminDockForced = useCircleAIShellStore(s => s.setAdminDockForced);
  const setMode = useCircleAIShellStore(s => s.setMode);
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();
  const isAdminArea = useIsAdminArea();
  const { isAiRoute, isAiIndexRoute } = useIsCircleAiPageRoute();
  const activeConversationId = useCircleAiStore(s => s.activeConversation.id);
  const { openNewChat } = useOpenCircleAi();

  const isConversationPanelOpen = conversationPanelState !== "closed";
  const isCircleAiEnabled = useIsCircleAiEnabled();
  const canUseCircleAi =
    isCircleAiEnabled && isAdmin && !isViewOnlyMasquerading;

  const toggleConversationPanel = () => {
    const willOpen = !isConversationPanelOpen;
    setConversationPanelState(willOpen ? "default" : "closed");
    if (isAdmin) {
      setAdminDockForced(willOpen);
      if (!willOpen && !isAdminArea && !isAiRoute) setMode("community");
    }
  };

  const items: DockNavSection["items"] = [];

  if (canUseCircleAi) {
    if (!isConversationPanelOpen && !isAdminArea && !isAiRoute) {
      items.push({
        id: "circle-ai",
        label: t("circle_ai_dock.primary.circle_ai"),
        icon: "sparkles",
        activeIcon: "sparkles-filled",
        isActive: isConversationPanelOpen,
        onClick: toggleConversationPanel,
        "data-testid": "circle-ai-shell-dock-ai-toggle",
      });
    }
    // Circle AI pages only render when the member-area AppShell is enabled.
    // Hide their links when that flag is off so the dock has no dead links.
    if (isMemberAreaAppShellEnabled) {
      items.push({
        id: "new-chat",
        label: t("circle_ai_dock.primary.new_chat"),
        icon: "circle-plus",
        activeIcon: "circle-plus-filled",
        // Highlight only on a genuinely blank new chat. The /ai page never moves
        // the conversation id into the URL, so path matching alone can't tell a
        // live chat from a new one — track the active conversation instead.
        isActive: isAiIndexRoute && !activeConversationId,
        onClick: () => openNewChat(),
      });
      items.push(getSkillsItem());
      items.push({
        id: "circle-ai-projects",
        label: t("circle_ai_dock.primary.projects"),
        icon: "folder",
        activeIcon: "folder-filled",
        to: "/ai/projects",
        exact: false,
      });
    }
  }

  if (isAdmin) {
    items.push({
      id: "inbox",
      label: t("circle_ai_dock.primary.inbox"),
      icon: "inbox-empty",
      activeIcon: "inbox-empty",
      to: settingsNavPathV3.unified_inbox.index,
      exact: false,
    });
  }

  return {
    id: "primary",
    items,
  };
};

// Separate from usePrimaryNavSection because that hook is also called from
// useLogoMenuOptions, which doesn't render badges. Keeping badge queries here
// ensures they only fire when an actual dock component is mounted.
export const usePrimaryNavSectionWithBadge = (): DockNavSection => {
  const section = usePrimaryNavSection();
  const hasInboxUnread = useInboxHasUnread();
  return {
    ...section,
    items: section.items.map((item: DockNavItem) =>
      item.id === "inbox" ? { ...item, badge: hasInboxUnread } : item,
    ),
  };
};
